import type { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';

import {
  clientExerciseAssignResponseSchema,
  clientExerciseDetailResponseSchema,
  clientExerciseSchema,
  createExerciseInputSchema,
  exerciseReviewQueueResponseSchema,
  exerciseSchema,
  requestExerciseThumbnailUploadUrlSchema,
  reviewExerciseResponseSchema,
  templateUploadUrlResponseSchema,
  updateExerciseInputSchema,
  type AssignExerciseInput,
  type ClientExercise,
  type ClientExerciseDetail,
  type ClientExerciseListParams,
  type CreateExerciseInput,
  type Exercise,
  type ExerciseListParams,
  type ExerciseReviewQueueRow,
  type PaginatedClientExercises,
  type ReviewQueueStatus,
  type PaginatedExercises,
  type RequestExerciseThumbnailUploadUrlInput,
  type ReviewExerciseInput,
  type ReviewExerciseResponse,
  type TemplateUploadUrlResponse,
  type UpdateExerciseInput,
} from '../types';

function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[exercisesService] ${context} response failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({
      status: 0,
      code: 'SHAPE_MISMATCH',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

function validateInputOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first ? `${path ? `${path}: ` : ''}${first.message}` : 'Invalid input';
    throw new ApiError({
      status: 400,
      code: 'VALIDATION_ERROR',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

export const exercisesService = {
  async list(params: ExerciseListParams = {}): Promise<PaginatedExercises> {
    const { data } = await apiClient.get<{
      data: unknown[];
      pagination?: { page: number; limit: number; total: number };
    }>('/exercises', { params });
    const rows: Exercise[] = data.data.map((r) => parseOrThrow(exerciseSchema, r, 'list'));
    return {
      data: rows,
      pagination: data.pagination ?? { page: 1, limit: rows.length, total: rows.length },
    };
  },

  async get(id: string): Promise<Exercise> {
    const { data } = await apiClient.get<{ data: unknown }>(`/exercises/${id}`);
    return parseOrThrow(exerciseSchema, data.data, 'get');
  },

  async create(input: CreateExerciseInput): Promise<Exercise> {
    const body = validateInputOrThrow(createExerciseInputSchema, input, 'create');
    const { data } = await apiClient.post<{ data: unknown }>('/exercises', body);
    return parseOrThrow(exerciseSchema, data.data, 'create');
  },

  async update(id: string, input: UpdateExerciseInput): Promise<Exercise> {
    const body = validateInputOrThrow(updateExerciseInputSchema, input, 'update');
    const { data } = await apiClient.patch<{ data: unknown }>(`/exercises/${id}`, body);
    return parseOrThrow(exerciseSchema, data.data, 'update');
  },

  /**
   * Soft-archive a template. The BE returns 409 (`Conflict`) if any
   * `client_exercises` rows still reference this template with status
   * `pending` or `in_progress` — the caller should surface the message
   * rather than treat it as a generic failure.
   */
  async archive(id: string): Promise<void> {
    await apiClient.delete(`/exercises/${id}`);
  },

  async assignToClient(clientId: string, input: AssignExerciseInput): Promise<ClientExercise> {
    const { data } = await apiClient.post<{ data: unknown }>(
      `/clients/${clientId}/exercises`,
      input,
    );
    return parseOrThrow(clientExerciseAssignResponseSchema, data.data, 'assignToClient');
  },

  async listClientExercises(
    clientId: string,
    params: ClientExerciseListParams = {},
  ): Promise<PaginatedClientExercises> {
    const { data } = await apiClient.get<{
      data: unknown[];
      pagination?: { page: number; limit: number; total: number };
    }>(`/clients/${clientId}/exercises`, { params });
    const rows: ClientExercise[] = data.data.map((r) =>
      parseOrThrow(clientExerciseSchema, r, 'listClientExercises'),
    );
    return {
      data: rows,
      pagination: data.pagination ?? { page: 1, limit: rows.length, total: rows.length },
    };
  },

  async getClientExercise(
    clientId: string,
    clientExerciseId: string,
  ): Promise<ClientExerciseDetail> {
    const { data } = await apiClient.get<{ data: unknown }>(
      `/clients/${clientId}/exercises/${clientExerciseId}`,
    );
    return parseOrThrow(clientExerciseDetailResponseSchema, data.data, 'getClientExercise');
  },

  async review(
    clientId: string,
    clientExerciseId: string,
    input: ReviewExerciseInput,
  ): Promise<ReviewExerciseResponse> {
    const { data } = await apiClient.post<{ data: unknown }>(
      `/clients/${clientId}/exercises/${clientExerciseId}/review`,
      input,
    );
    return parseOrThrow(reviewExerciseResponseSchema, data.data, 'review');
  },

  /**
   * Request a presigned S3 upload URL for a template thumbnail.
   *
   * The server persists the derived S3 key onto the exercise row in the same
   * transaction *before* returning the URL, so the FE never PATCHes the key
   * back. After the subsequent direct `PUT` to `uploadUrl` succeeds, refetch
   * the exercise detail to receive the new `thumbnailDownloadUrl`.
   */
  async requestThumbnailUploadUrl(
    input: RequestExerciseThumbnailUploadUrlInput,
  ): Promise<TemplateUploadUrlResponse> {
    const body = validateInputOrThrow(
      requestExerciseThumbnailUploadUrlSchema,
      input,
      'requestThumbnailUploadUrl',
    );
    const { data } = await apiClient.post<{ data: unknown }>(
      '/exercises/thumbnail-upload-url',
      body,
    );
    return parseOrThrow(templateUploadUrlResponseSchema, data.data, 'requestThumbnailUploadUrl');
  },

  /**
   * GET /exercises/review-queue — exercises the practitioner has assigned
   * that the client has completed and that nobody has reviewed yet.
   * Optional `organizationId` / `locationId` come from the global tenant
   * scope picker.
   */
  async listReviewQueue(
    params: {
      organizationId?: string;
      locationId?: string;
      status?: ReviewQueueStatus;
      search?: string;
      clientId?: string;
    } = {},
  ): Promise<ExerciseReviewQueueRow[]> {
    const { data } = await apiClient.get<{ data: unknown }>('/exercises/review-queue', { params });
    return parseOrThrow(exerciseReviewQueueResponseSchema, data.data, 'listReviewQueue');
  },
};
